// Modules
import { JSX, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormattedMessage } from "react-intl";
// Material-UI
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Stack,
    Grid,
    TableContainer,
    Alert,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Typography,
    AvatarGroup,
    Avatar,
    IconButton
} from "@mui/material";
// Actions
import { getTherapistsWithEvents } from "@/actions/shared/calendarManagementActions";
import { deleteSubstitute, getPatientEventsSWR, PatientSubstitutesSWR } from "@/actions/patient/calendarActions";
import { PatientNotificationsSWR } from "@/actions/patient/notificationActions";
import { openSnackbar } from "@/config/store/snackbarReducer";
// Helpers
import api from "@/helpers/api";
import { API_ROUTES } from "@/helpers/routes";
import { formatDate } from "@/helpers/dates";
import { getTherapist, getUserData } from "@/helpers/userData";
// Components
import SubstitutesEditDialog from "./SubstitutesEditDialog";
import AlertDialog from "@/components/UITemplate/extended/AlertDialog";
// Types
import { SubstituteType } from "@/lib/types/types";
// Assets
import DateRangeIcon from "@mui/icons-material/DateRange";
import ContentPasteSearchIcon from "@mui/icons-material/ContentPasteSearch";
import DeleteTwoTone from "@mui/icons-material/DeleteTwoTone";

interface SubstituteModalProps {
    isOpen: boolean;
    closeModal: (modalName: string) => void;
}

type EditSubstituteModalState = {
    opened: boolean;
    substitute?: SubstituteType;
};

type DeleteModalState = {
    opened: boolean;
    action?: () => void;
    title?: string;
    confirmation?: string;
    amount?: number;
};

const dateFormatOptions: Intl.DateTimeFormatOptions = { month: "short" };

const SubstitutesDialog = ({ isOpen, closeModal }: SubstituteModalProps) => {
    const dispatch = useDispatch();
    const { locale } = useSelector((state: { customization: { locale: string } }) => state.customization);

    const parent = getUserData();
    const assignedTherapist = getTherapist();

    const [editSubstituteModal, setEditSubstituteModal] = useState<EditSubstituteModalState>({ opened: false });
    const [deleteModal, setDeleteModal] = useState<DeleteModalState>({ opened: false });

    const { refreshTherapistsWithEvents } = getTherapistsWithEvents();
    const { refreshEvents } = getPatientEventsSWR(parent?._id ?? "");
    const { substitutes, refreshSubstitutes } = PatientSubstitutesSWR(parent?._id ?? "");
    const { refreshNotifications } = PatientNotificationsSWR();

    const toggleSubstitutesModal = () => {
        closeModal("substitute");
    };

    const toggleDeleteModal = (
        opened: boolean,
        options?: {
            action?: () => void;
            title?: string;
            confirmation?: string;
            amount?: number;
        }
    ) => {
        if (opened) {
            setDeleteModal({
                opened: true,
                action: options?.action!,
                title: options?.title || "",
                confirmation: options?.confirmation || "",
                amount: options?.amount || 0
            });
        } else {
            setDeleteModal(prev => ({ ...prev, opened: false }));
            setTimeout(() => {
                setDeleteModal(prev => ({ ...prev, action: undefined }));
            }, 500);
        }
    };

    const handleOpenEditSubstituteModal = (substitute: SubstituteType) => {
        setEditSubstituteModal({ opened: true, substitute });
    };
    const handleCloseEditSubstituteModal = () => {
        setEditSubstituteModal({ opened: false, substitute: undefined });
    };

    const triggerDataFetch = () => {
        refreshTherapistsWithEvents();
        refreshEvents();
        refreshSubstitutes();
        refreshNotifications();
        toggleDeleteModal(false);
    };

    const triggerSnackbar = (alertSeverity: string, message: JSX.Element) =>
        dispatch(
            openSnackbar({
                message,
                alertSeverity
            })
        );

    const handleRemindLater = async () => {
        api.post({
            path: API_ROUTES.patient.substitutes.postpone(parent?._id ?? "")
        });
        toggleSubstitutesModal();
    };

    const handleSubstituteDelete = (id: string) => {
        deleteSubstitute &&
            deleteSubstitute({
                patientId: parent?._id ?? "",
                substituteId: id,
                data: { action: "delete", assignedTherapist },
                dispatch,
                successCb: (availableSubsitutes: any) => {
                    if (availableSubsitutes.length === 0) {
                        closeModal("substitute");
                    }
                    triggerSnackbar("success", <FormattedMessage id={"success-substitute-delete"} />);
                    triggerDataFetch();
                },
                errorCb: () => {
                    triggerSnackbar("error", <FormattedMessage id={"error-substitute-delete"} />);
                }
            });
    };

    const availableSessions = substitutes?.reduce(
        (sum: any, item: { available_sessions: any }) => sum + item.available_sessions,
        0
    );
    const activeSubstitute = substitutes?.find((sub: any) => sub.id === editSubstituteModal?.substitute?.id);

    return (
        <>
            <Dialog open={isOpen} onClose={toggleSubstitutesModal} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ p: 3 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <FormattedMessage id="available-substitutes-title" />

                        <Button onClick={handleRemindLater}>
                            <FormattedMessage id="recurring-feedback-remind-later" />
                        </Button>
                    </Box>
                    <Alert icon={false} severity="info" sx={{ mt: 2 }}>
                        <FormattedMessage
                            id="amount-of-available-sessions-info"
                            values={{ amount: <b>{availableSessions}</b> }}
                        />
                    </Alert>
                </DialogTitle>

                <DialogContent>
                    <Grid container justifyContent="center">
                        <TableContainer>
                            {substitutes?.length > 0 && (
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ pl: 1 }}>
                                                <FormattedMessage id="holiday-period-table-title" />
                                            </TableCell>
                                            <TableCell sx={{ pl: 1 }}>
                                                <FormattedMessage id="therapists" />
                                            </TableCell>
                                            <TableCell align="right" sx={{ pr: 3 }}></TableCell>
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {substitutes?.map((row: SubstituteType, index: number) => (
                                            <TableRow hover key={index}>
                                                <TableCell sx={{ pl: 3 }}>
                                                    <Grid
                                                        container
                                                        spacing={2}
                                                        alignItems="center"
                                                        sx={{ flexWrap: "nowrap" }}
                                                    >
                                                        <Grid item>
                                                            <DateRangeIcon />
                                                        </Grid>
                                                        <Grid item xs zeroMinWidth>
                                                            <Typography
                                                                component="div"
                                                                align="left"
                                                                variant="subtitle1"
                                                            >
                                                                {formatDate(row.start, dateFormatOptions, locale)} -{" "}
                                                                {formatDate(row.end, dateFormatOptions, locale)}
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                </TableCell>

                                                <TableCell>
                                                    <Grid container justifyContent="left">
                                                        <AvatarGroup max={4}>
                                                            {row.therapists.map((therapist, index) => (
                                                                <Avatar key={index} alt={therapist.name} />
                                                            ))}
                                                        </AvatarGroup>
                                                    </Grid>
                                                </TableCell>
                                                <TableCell align="right" sx={{ pr: 3 }}>
                                                    <Stack direction="row" justifyContent="right">
                                                        <IconButton
                                                            color="primary"
                                                            size="large"
                                                            onClick={() => handleOpenEditSubstituteModal(row)}
                                                        >
                                                            <ContentPasteSearchIcon />
                                                        </IconButton>
                                                        <IconButton
                                                            color="primary"
                                                            size="large"
                                                            onClick={() =>
                                                                toggleDeleteModal(true, {
                                                                    action: () => handleSubstituteDelete(row?.id),
                                                                    title: "amount-of-available-sessions",
                                                                    confirmation:
                                                                        "confirm-subsitute-warning-confirmation",
                                                                    amount: row?.available_sessions || 0
                                                                })
                                                            }
                                                        >
                                                            <DeleteTwoTone />
                                                        </IconButton>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </TableContainer>
                        <AlertDialog
                            open={deleteModal?.opened || false}
                            handleClose={() => toggleDeleteModal(false)}
                            actions={{
                                primary: {
                                    action: deleteModal?.action || (() => {}),
                                    label: "yes"
                                },
                                secondary: {
                                    action: () => toggleDeleteModal(false),
                                    label: "cancel"
                                }
                            }}
                            title={deleteModal?.title}
                            value={deleteModal?.amount}
                            confirmation={deleteModal?.confirmation}
                        />
                        <SubstitutesEditDialog
                            open={editSubstituteModal?.opened || false}
                            parent={parent}
                            substitute={activeSubstitute}
                            handleCloseDialog={handleCloseEditSubstituteModal}
                            closeModal={closeModal}
                            triggerDataFetch={triggerDataFetch}
                        />
                    </Grid>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default SubstitutesDialog;
